import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { useUser } from '@/context/UserContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Filter, Play, Building2, Save, XCircle, FolderOpen, Pencil, Check, X, ChevronDown, Search, Download } from 'lucide-react';
import { toast } from 'sonner';
import { ActionBadge, EtatBadge, LodeomBadge } from './Dashboard';

const ACTIONS = ["A appeler", "A rappeler", "RDV pris", "RDV realise"];
const ETATS = ["A renseigner", "Propale en negociation", "Perdu", "Concu", "RAF", "Client RH", "Client FIPRO", "Client PMS", "A renouveler"];
const REGIONS = ['Guadeloupe', 'Martinique', 'Guyane', 'Saint-Martin', 'Saint-Barthelemy'];
const EFFECTIFS = ['1-10', '11-49', '50-199', '200+'];
const LODEOM_OPTIONS = ['HL', 'Competitivite', 'Competitivite renforcee', 'Innovation et croissance'];
const SITUATIONS = [
  { value: 'Actif', label: 'Actif' },
  { value: 'RJ', label: 'RJ (Redressement judiciaire)' },
  { value: 'LJ', label: 'LJ (Liquidation judiciaire)' },
  { value: 'Plan de sauvegarde', label: 'Plan de sauvegarde' },
  { value: 'Plan de continuation', label: 'Plan de continuation' },
];

// ── Reusable Multi-Select Dropdown ──
const MultiDropdown = ({ label, options, selected, onChange, searchable, testId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(opt => {
    if (!search) return true;
    const s = search.toLowerCase();
    const lbl = typeof opt === 'string' ? opt : opt.label;
    const val = typeof opt === 'string' ? opt : opt.value;
    return lbl.toLowerCase().includes(s) || val.toLowerCase().includes(s);
  });

  const getValue = (opt) => typeof opt === 'string' ? opt : opt.value;
  const getLabel = (opt) => typeof opt === 'string' ? opt : opt.label;

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const selectAll = () => onChange(options.map(getValue));
  const clearAll = () => onChange([]);

  const displayText = selected.length === 0
    ? `Tous`
    : selected.length === 1
      ? getLabel(options.find(o => getValue(o) === selected[0]) || selected[0])
      : `${selected.length} selectionnes`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between gap-2 h-9 px-3 border rounded-md text-xs bg-white hover:bg-accent/30 transition-colors ${selected.length > 0 ? 'border-[#1A4D2E]/40 text-[#1A4D2E] font-medium' : 'border-border text-gray-600'}`}
        data-testid={testId}
      >
        <span className="truncate">{displayText}</span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[200px] bg-white border border-border rounded-lg shadow-lg" data-testid={`${testId}-dropdown`}>
          {searchable && (
            <div className="p-2 border-b border-border/50">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-7 pr-2 py-1.5 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-[#1A4D2E]/30"
                  data-testid={`${testId}-search`}
                />
              </div>
            </div>
          )}
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50">
            <button onClick={selectAll} className="text-[10px] text-[#1A4D2E] font-medium hover:underline" data-testid={`${testId}-all`}>Tout</button>
            <button onClick={clearAll} className="text-[10px] text-red-500 font-medium hover:underline" data-testid={`${testId}-none`}>Aucun</button>
          </div>
          <div className="max-h-[200px] overflow-y-auto p-1">
            {filtered.map(opt => {
              const val = getValue(opt);
              const lbl = getLabel(opt);
              return (
                <label key={val} className="flex items-center gap-2 py-1 px-2 hover:bg-accent/30 rounded cursor-pointer">
                  <input type="checkbox" checked={selected.includes(val)} onChange={() => toggle(val)} className="rounded accent-[#1A4D2E]" />
                  <span className="text-xs truncate">{lbl}</span>
                </label>
              );
            })}
            {filtered.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">Aucun resultat</p>}
          </div>
        </div>
      )}
    </div>
  );
};

// ── NAF Multi-Select (special: has descriptions) ──
const NafDropdown = ({ allNafCodes, nafDescriptions, selected, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = allNafCodes.filter(code => {
    if (!search) return true;
    const s = search.toLowerCase();
    return code.toLowerCase().includes(s) || (nafDescriptions[code] || '').toLowerCase().includes(s);
  });

  const displayText = selected.length === 0
    ? 'Tous les codes NAF'
    : selected.length <= 2
      ? selected.join(', ')
      : `${selected.length} codes NAF`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between gap-2 h-9 px-3 border rounded-md text-xs bg-white hover:bg-accent/30 transition-colors ${selected.length > 0 ? 'border-[#1A4D2E]/40 text-[#1A4D2E] font-medium' : 'border-border text-gray-600'}`}
        data-testid={testId}
      >
        <span className="truncate">{displayText}</span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[350px] bg-white border border-border rounded-lg shadow-lg" data-testid={`${testId}-dropdown`}>
          <div className="p-2 border-b border-border/50">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="text"
                placeholder="Rechercher un code NAF..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-[#1A4D2E]/30"
                data-testid={`${testId}-search`}
              />
            </div>
          </div>
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50">
            <button onClick={() => onChange([...allNafCodes])} className="text-[10px] text-[#1A4D2E] font-medium hover:underline">Tout</button>
            <button onClick={() => onChange([])} className="text-[10px] text-red-500 font-medium hover:underline">Aucun</button>
          </div>
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1 px-2 py-1.5 border-b border-border/50 max-h-[60px] overflow-y-auto">
              {selected.map(code => (
                <span key={code} className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {code}
                  <button onClick={() => onChange(selected.filter(c => c !== code))} className="hover:text-red-600"><X className="h-2 w-2" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="max-h-[200px] overflow-y-auto p-1">
            {filtered.map(code => (
              <label key={code} className="flex items-center gap-2 py-0.5 px-2 hover:bg-accent/30 rounded cursor-pointer">
                <input type="checkbox" checked={selected.includes(code)} onChange={() => onChange(selected.includes(code) ? selected.filter(c => c !== code) : [...selected, code])} className="rounded accent-[#1A4D2E]" />
                <span className="text-xs font-mono shrink-0">{code}</span>
                {nafDescriptions[code] && <span className="text-[10px] text-muted-foreground truncate">{nafDescriptions[code]}</span>}
              </label>
            ))}
            {filtered.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">Aucun code NAF trouve</p>}
          </div>
        </div>
      )}
    </div>
  );
};

const Prospection = () => {
  const { canEdit, isDirector } = useUser();
  const [tab, setTab] = useState('new');
  const [step, setStep] = useState('criteria');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [nafDescriptions, setNafDescriptions] = useState({});

  // All filters are arrays now
  const [filterRegion, setFilterRegion] = useState([]);
  const [filterNaf, setFilterNaf] = useState([]);
  const [filterLodeom, setFilterLodeom] = useState([]);
  const [filterEffectif, setFilterEffectif] = useState([]);
  const [filterAction, setFilterAction] = useState([]);
  const [filterEtat, setFilterEtat] = useState([]);
  const [filterTag, setFilterTag] = useState([]);
  const [filterSituation, setFilterSituation] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [allNafCodes, setAllNafCodes] = useState([]);

  // Qualif editing
  const [editingRow, setEditingRow] = useState(null);
  const [editContact, setEditContact] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const toFilter = (arr) => arr.length > 0 ? arr : 'all';

  const fetchFilterOptions = useCallback(async () => {
    try {
      const [tagsRes, companiesRes, nafRes] = await Promise.all([
        axios.get(`${API}/tags`, { headers: headers() }),
        axios.get(`${API}/companies`, { headers: headers() }),
        axios.get(`${API}/naf/descriptions`, { headers: headers() }).catch(() => ({ data: {} })),
      ]);
      setAllTags(tagsRes.data);
      const nafs = [...new Set(companiesRes.data.map(c => c.sector).filter(Boolean))].sort();
      setAllNafCodes(nafs);
      setNafDescriptions(nafRes.data);
    } catch (e) { /* silent */ }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/prospection/sessions`, { headers: headers() });
      setSessions(res.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
    fetchSessions();
  }, [fetchFilterOptions, fetchSessions]);

  const handleLaunchSession = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/prospection/session`, {
        region: toFilter(filterRegion),
        naf: toFilter(filterNaf),
        lodeom: toFilter(filterLodeom),
        effectif: toFilter(filterEffectif),
        action: toFilter(filterAction),
        etat: toFilter(filterEtat),
        tag: toFilter(filterTag),
        situation: toFilter(filterSituation),
      }, { headers: headers() });
      setRows(res.data);
      setStep('list');
      setCurrentSessionId(null);
      if (res.data.length === 0) toast.info('Aucune entreprise ne correspond a ces criteres');
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSession = async () => {
    try {
      const res = await axios.post(`${API}/prospection/sessions/save`, {
        filters: {
          region: filterRegion, naf: filterNaf, lodeom: filterLodeom,
          effectif: filterEffectif, action: filterAction, etat: filterEtat,
          tag: filterTag, situation: filterSituation,
        },
        company_ids: rows.map(r => r.id),
      }, { headers: headers() });
      setCurrentSessionId(res.data.id);
      toast.success(`Session sauvegardee: ${res.data.name}`);
      fetchSessions();
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleCloseSession = async (sessionId) => {
    const id = sessionId || currentSessionId;
    if (!id) { setStep('criteria'); return; }
    try {
      await axios.put(`${API}/prospection/sessions/${id}/close`, {}, { headers: headers() });
      toast.success('Session terminee');
      setStep('criteria');
      setCurrentSessionId(null);
      setRows([]);
      fetchSessions();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleLoadSession = async (session) => {
    setLoading(true);
    try {
      const f = session.filters || {};
      setFilterRegion(f.region || []);
      setFilterNaf(f.naf || []);
      setFilterLodeom(f.lodeom || []);
      setFilterEffectif(f.effectif || []);
      setFilterAction(f.action || []);
      setFilterEtat(f.etat || []);
      setFilterTag(f.tag || []);
      setFilterSituation(f.situation || []);

      const res = await axios.post(`${API}/prospection/session`, {
        region: toFilter(f.region || []),
        naf: toFilter(f.naf || []),
        lodeom: toFilter(f.lodeom || []),
        effectif: toFilter(f.effectif || []),
        action: toFilter(f.action || []),
        etat: toFilter(f.etat || []),
        tag: toFilter(f.tag || []),
        situation: toFilter(f.situation || []),
      }, { headers: headers() });

      const savedIds = new Set(session.company_ids || []);
      const filtered = savedIds.size > 0 ? res.data.filter(r => savedIds.has(r.id)) : res.data;
      setRows(filtered);
      setCurrentSessionId(session.id);
      setStep('list');
      setTab('new');
    } catch (e) {
      toast.error('Erreur chargement session');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAction = async (companyId, newAction) => {
    try {
      await axios.patch(`${API}/companies/${companyId}/status`, { action: newAction }, { headers: headers() });
      setRows(prev => prev.map(r => r.id === companyId ? { ...r, action: newAction } : r));
    } catch (e) { toast.error('Erreur'); }
  };

  const handleUpdateEtat = async (companyId, newEtat) => {
    try {
      await axios.patch(`${API}/companies/${companyId}/status`, { etat_compte: newEtat }, { headers: headers() });
      setRows(prev => prev.map(r => r.id === companyId ? { ...r, etat_compte: newEtat } : r));
    } catch (e) { toast.error('Erreur'); }
  };

  const handleSelectContact = (companyId, contactId) => {
    setRows(prev => prev.map(r => {
      if (r.id !== companyId) return r;
      const ct = r.contacts.find(c => c.id === contactId);
      if (!ct) return r;
      return { ...r, selected_contact_id: contactId, contact_name: ct.name, contact_phone: ct.phone, contact_email: ct.email };
    }));
  };

  const startQualif = (row) => {
    setEditingRow(row.id);
    setEditContact(row.contact_name || '');
    setEditPhone(row.contact_phone || '');
    setEditEmail(row.contact_email || '');
  };

  const saveQualif = async (row) => {
    try {
      if (row.selected_contact_id) {
        await axios.patch(`${API}/contacts/${row.selected_contact_id}/info`, {
          phone: editPhone || undefined,
          email: editEmail || undefined,
        }, { headers: headers() });
      } else {
        const res = await axios.post(`${API}/contacts`, {
          company_id: row.id,
          name: editContact.trim() || 'Contact',
          position: '',
          phone: editPhone || null,
          email: editEmail || null,
        }, { headers: headers() });
        const newCt = { id: res.data.id, name: editContact, phone: editPhone, email: editEmail };
        setRows(prev => prev.map(r => r.id === row.id ? {
          ...r, contacts: [...r.contacts, newCt], selected_contact_id: res.data.id,
          contact_name: editContact, contact_phone: editPhone, contact_email: editEmail
        } : r));
      }
      setRows(prev => prev.map(r => r.id === row.id ? {
        ...r, contact_name: editContact, contact_phone: editPhone, contact_email: editEmail
      } : r));
      setEditingRow(null);
      toast.success('Contact mis a jour');
    } catch (e) {
      toast.error('Erreur mise a jour contact');
    }
  };

  const formatEffectif = (size) => {
    if (!size || size === 0) return '-';
    if (size >= 1000) return size.toLocaleString('fr-FR');
    return String(size);
  };

  const handleExportExcel = () => {
    import('xlsx').then(XLSX => {
      const data = rows.map(row => ({
        'Entreprise': row.name,
        'SIREN': row.siren,
        'Region': row.region || '',
        'Effectif': row.size || '',
        'Bareme LODEOM': row.bareme_lodeom || '',
        'Secteur (NAF)': row.sector || '',
        'Description NAF': nafDescriptions[row.sector] || '',
        'Contact': row.contact_name || '',
        'Telephone': row.contact_phone || '',
        'Email': row.contact_email || '',
        'Etat': row.etat_compte || '',
        'Action': row.action || '',
        'Situation': row.situation_juridique || 'Actif',
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const colWidths = [
        { wch: 30 }, { wch: 12 }, { wch: 15 }, { wch: 8 }, { wch: 22 },
        { wch: 10 }, { wch: 35 }, { wch: 20 }, { wch: 15 }, { wch: 25 },
        { wch: 20 }, { wch: 12 }, { wch: 18 },
      ];
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Prospection');
      const now = new Date();
      const dateStr = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}`;
      XLSX.writeFile(wb, `SSCaraibes_Prospection_${dateStr}.xlsx`);
      toast.success(`Export Excel: ${data.length} entreprises`);
    });
  };

  const activeFiltersCount = [filterRegion, filterNaf, filterLodeom, filterEffectif, filterAction, filterEtat, filterTag, filterSituation]
    .filter(arr => arr.length > 0).length;

  return (
    <div className="min-h-screen bg-[#f1f5f9]">
      <Navbar />
      <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-border">
          <button
            onClick={() => setTab('new')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${tab === 'new' ? 'border-[#1A4D2E] text-[#1A4D2E]' : 'border-transparent text-muted-foreground hover:text-gray-700'}`}
            data-testid="tab-new-session"
          >
            Nouvelle session
          </button>
          <button
            onClick={() => { setTab('saved'); fetchSessions(); }}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${tab === 'saved' ? 'border-[#1A4D2E] text-[#1A4D2E]' : 'border-transparent text-muted-foreground hover:text-gray-700'}`}
            data-testid="tab-saved-sessions"
          >
            <FolderOpen className="h-4 w-4" />
            Sessions sauvegardees
            {sessions.length > 0 && <span className="text-[10px] bg-[#1A4D2E] text-white rounded-full px-1.5">{sessions.length}</span>}
          </button>
        </div>

        {/* Saved Sessions Tab */}
        {tab === 'saved' && (
          <div className="space-y-3" data-testid="saved-sessions-list">
            {sessions.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-border/50">
                <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Aucune session sauvegardee</p>
              </div>
            ) : (
              sessions.map(s => (
                <div key={s.id} className="bg-white rounded-xl border border-border/50 shadow-sm p-4 flex items-center justify-between" data-testid="saved-session-item">
                  <div>
                    <p className="font-semibold text-sm">{s.name}</p>
                    <p className="text-xs text-muted-foreground">{s.company_ids?.length || 0} entreprises - par {s.created_by}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleLoadSession(s)} className="bg-[#1A4D2E] text-white hover:bg-[#153d24] rounded-full gap-1 text-xs" data-testid="load-session-btn">
                      <Play className="h-3 w-3" /> Reprendre
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleCloseSession(s.id)} className="rounded-full gap-1 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="close-session-btn">
                      <XCircle className="h-3 w-3" /> Terminer
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* New Session Tab - Criteria */}
        {tab === 'new' && step === 'criteria' && (
          <>
            <div className="mb-6">
              <h1 className="text-3xl font-heading font-bold text-gray-900" data-testid="prospection-title">
                Session de prospection
              </h1>
              <p className="text-muted-foreground mt-1">Selectionnez vos criteres pour lancer la session</p>
            </div>

            <div className="bg-white rounded-xl border border-border/50 shadow-sm p-6" data-testid="criteria-panel">
              <div className="flex items-center gap-2 mb-6">
                <Filter className="h-5 w-5 text-[#1A4D2E]" />
                <h2 className="font-heading font-semibold text-lg">Criteres de la session</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Region</label>
                  <MultiDropdown
                    options={REGIONS}
                    selected={filterRegion}
                    onChange={setFilterRegion}
                    testId="prosp-filter-region"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Codes NAF {filterNaf.length > 0 && <span className="text-[#1A4D2E]">({filterNaf.length})</span>}
                  </label>
                  <NafDropdown
                    allNafCodes={allNafCodes}
                    nafDescriptions={nafDescriptions}
                    selected={filterNaf}
                    onChange={setFilterNaf}
                    testId="prosp-filter-naf"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Bareme LODEOM</label>
                  <MultiDropdown
                    options={LODEOM_OPTIONS}
                    selected={filterLodeom}
                    onChange={setFilterLodeom}
                    testId="prosp-filter-lodeom"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Effectif</label>
                  <MultiDropdown
                    options={EFFECTIFS}
                    selected={filterEffectif}
                    onChange={setFilterEffectif}
                    testId="prosp-filter-effectif"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Action</label>
                  <MultiDropdown
                    options={ACTIONS}
                    selected={filterAction}
                    onChange={setFilterAction}
                    testId="prosp-filter-action"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Etat</label>
                  <MultiDropdown
                    options={ETATS}
                    selected={filterEtat}
                    onChange={setFilterEtat}
                    testId="prosp-filter-etat"
                  />
                </div>
                {allTags.length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Tag</label>
                    <MultiDropdown
                      options={allTags}
                      selected={filterTag}
                      onChange={setFilterTag}
                      testId="prosp-filter-tag"
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Situation du compte</label>
                  <MultiDropdown
                    options={SITUATIONS}
                    selected={filterSituation}
                    onChange={setFilterSituation}
                    testId="prosp-filter-situation"
                  />
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {activeFiltersCount > 0 ? `${activeFiltersCount} critere${activeFiltersCount > 1 ? 's' : ''} selectionne${activeFiltersCount > 1 ? 's' : ''}` : 'Aucun filtre'}
                </span>
                <Button onClick={handleLaunchSession} disabled={loading} className="bg-[#1A4D2E] text-white hover:bg-[#153d24] rounded-full gap-2 px-6" data-testid="launch-session-btn">
                  <Play className="h-4 w-4" />
                  {loading ? 'Chargement...' : 'Lancer la session'}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Session List */}
        {tab === 'new' && step === 'list' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-heading font-bold text-gray-900" data-testid="prospection-list-title">
                  Session en cours
                </h1>
                <p className="text-muted-foreground text-sm mt-1">{rows.length} entreprise{rows.length > 1 ? 's' : ''}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep('criteria')} className="rounded-full gap-2 text-sm" data-testid="back-criteria-btn">
                  <Filter className="h-4 w-4" /> Criteres
                </Button>
                {isDirector && (
                  <Button variant="outline" onClick={handleExportExcel} className="rounded-full gap-2 text-sm text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="export-excel-btn">
                    <Download className="h-4 w-4" /> Export Excel
                  </Button>
                )}
                <Button onClick={handleSaveSession} className="bg-[#1A4D2E] text-white hover:bg-[#153d24] rounded-full gap-2 text-sm" data-testid="save-session-btn">
                  <Save className="h-4 w-4" /> Sauvegarder
                </Button>
                <Button variant="outline" onClick={() => handleCloseSession()} className="rounded-full gap-2 text-sm text-red-600 border-red-200 hover:bg-red-50" data-testid="end-session-btn">
                  <XCircle className="h-4 w-4" /> Fin de session
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-border/50 shadow-sm overflow-hidden" data-testid="prospection-table">
              {rows.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucune entreprise</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-accent/30 border-b border-border">
                      <tr>
                        <th className="text-left px-3 py-3 text-xs font-semibold text-gray-700">Entreprise</th>
                        <th className="text-left px-3 py-3 text-xs font-semibold text-gray-700">SIREN</th>
                        <th className="text-left px-3 py-3 text-xs font-semibold text-gray-700">Region</th>
                        <th className="text-left px-2 py-3 text-xs font-semibold text-gray-700">Effectif</th>
                        <th className="text-left px-2 py-3 text-xs font-semibold text-gray-700">LODEOM</th>
                        <th className="text-left px-3 py-3 text-xs font-semibold text-gray-700">Secteur</th>
                        <th className="text-left px-3 py-3 text-xs font-semibold text-gray-700">Contact</th>
                        <th className="text-left px-3 py-3 text-xs font-semibold text-gray-700">Numero</th>
                        <th className="text-left px-3 py-3 text-xs font-semibold text-gray-700">Mail</th>
                        <th className="text-left px-3 py-3 text-xs font-semibold text-gray-700">Etat</th>
                        <th className="text-left px-3 py-3 text-xs font-semibold text-gray-700">Action</th>
                        {canEdit && <th className="text-center px-2 py-3 text-xs font-semibold text-gray-700">Qualif</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id} className="border-b border-border/50 hover:bg-accent/10 transition-colors" data-testid="prospection-row">
                          <td className="px-3 py-2">
                            <Link to={`/companies/${row.id}`} className="text-sm font-semibold text-gray-900 hover:text-[#1A4D2E] transition-colors no-underline inline-flex items-center gap-1" data-testid="prosp-company-link">
                              {row.name}
                              {row.situation_juridique && row.situation_juridique !== 'Actif' && (
                                <span className="text-red-500 text-xs font-bold" title={row.situation_juridique} data-testid="situation-indicator">*</span>
                              )}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 font-mono">{row.siren}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{row.region || '-'}</td>
                          <td className="px-2 py-2 text-xs text-gray-600">{formatEffectif(row.size)}</td>
                          <td className="px-2 py-2">
                            <LodeomBadge value={row.bareme_lodeom} alt={row.bareme_lodeom_alt} />
                          </td>
                          <td className="px-3 py-2">
                            <span className="text-[10px] text-gray-600 font-mono">{row.sector || '-'}</span>
                            {row.sector && nafDescriptions[row.sector] && (
                              <p className="text-[9px] text-muted-foreground truncate max-w-[120px]">{nafDescriptions[row.sector]}</p>
                            )}
                          </td>

                          {editingRow === row.id ? (
                            <>
                              <td className="px-2 py-1">
                                <Input value={editContact} onChange={(e) => setEditContact(e.target.value)} className="h-7 text-xs" placeholder="Nom..." data-testid="qualif-contact-input" />
                              </td>
                              <td className="px-2 py-1">
                                <Input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} className="h-7 text-xs" placeholder="Tel..." data-testid="qualif-phone-input" />
                              </td>
                              <td className="px-2 py-1">
                                <Input value={editEmail} onChange={(e) => setEditEmail(e.target.value)} className="h-7 text-xs" placeholder="Email..." data-testid="qualif-email-input" />
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-3 py-2">
                                {row.contacts.length > 1 ? (
                                  <select value={row.selected_contact_id || ''} onChange={(e) => handleSelectContact(row.id, e.target.value)} className="text-xs border border-border rounded px-1 py-0.5 bg-white max-w-[130px]" data-testid="prosp-contact-select">
                                    {row.contacts.map(ct => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
                                  </select>
                                ) : (
                                  <span className="text-xs text-gray-600">{row.contact_name || '-'}</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600">{row.contact_phone || '-'}</td>
                              <td className="px-3 py-2 text-xs text-gray-600 truncate max-w-[140px]">{row.contact_email || '-'}</td>
                            </>
                          )}

                          <td className="px-2 py-2">
                            {canEdit ? (
                              <select value={row.etat_compte} onChange={(e) => handleUpdateEtat(row.id, e.target.value)} className="text-[10px] font-medium border border-border rounded px-1 py-0.5 bg-white max-w-[130px]" data-testid="prosp-etat-select">
                                {ETATS.map(e => <option key={e} value={e}>{e}</option>)}
                              </select>
                            ) : <EtatBadge value={row.etat_compte} />}
                          </td>
                          <td className="px-2 py-2">
                            {canEdit ? (
                              <select value={row.action} onChange={(e) => handleUpdateAction(row.id, e.target.value)} className="text-[10px] font-medium border border-border rounded px-1 py-0.5 bg-white max-w-[110px]" data-testid="prosp-action-select">
                                {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                              </select>
                            ) : <ActionBadge value={row.action} />}
                          </td>

                          {canEdit && (
                            <td className="px-2 py-2 text-center">
                              {editingRow === row.id ? (
                                <div className="flex gap-1 justify-center">
                                  <button onClick={() => saveQualif(row)} className="text-emerald-600 hover:text-emerald-800" data-testid="qualif-save-btn"><Check className="h-4 w-4" /></button>
                                  <button onClick={() => setEditingRow(null)} className="text-red-500 hover:text-red-700" data-testid="qualif-cancel-btn"><X className="h-4 w-4" /></button>
                                </div>
                              ) : (
                                <button onClick={() => startQualif(row)} className="text-[#1A4D2E] hover:text-emerald-800" data-testid="qualif-btn">
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default Prospection;
